/**
 * Hook for fetching entity verification analysis data.
 *
 * When an auto-mode pipeline is in-flight, the backend returns a
 * ClinicalNotesAnalysis document with a non-terminal `status`
 * (`pending` / `extracting` / `awaiting_note` / `verifying`) and an empty
 * `entity_verification_data`. This hook polls every `IN_FLIGHT_POLL_MS` while
 * such a document is present so the UI can react when it transitions to
 * `completed` or `failed`.
 */

import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { getSessionAnalysis } from '@/pages/api/scribeAPIV2';
import {
  isAnalysisInFlight,
  type ClinicalNotesAnalysis,
} from '@/types/entity-verification';
import { logger } from '@/utils/logger/logger';

export const ENTITY_VERIFICATION_QUERY_KEY = 'entity_verification';

/**
 * Poll interval while an analysis is in-flight. 3s keeps perceived latency
 * low without hammering the BE — a typical auto run completes in 15-45s.
 */
const IN_FLIGHT_POLL_MS = 3_000;

interface UseQueryEntityVerificationOptions {
  enabled?: boolean;
}

export const useQueryEntityVerification = (
  sessionId: string,
  options?: UseQueryEntityVerificationOptions
) => {
  return useQuery<ClinicalNotesAnalysis | null>({
    queryKey: [ENTITY_VERIFICATION_QUERY_KEY, { sessionId }],
    queryFn: async () => {
      try {
        const { data } = await getSessionAnalysis(sessionId);
        return data;
      } catch (error) {
        if (error instanceof AxiosError) {
          logger.error('Error fetching entity verification:', error.response?.data);
        }
        throw error;
      }
    },
    enabled: options?.enabled !== false && !!sessionId,
    staleTime: 5 * 60 * 1000,
    refetchInterval: (query) => {
      const data = query.state.data;
      return isAnalysisInFlight(data?.status) ? IN_FLIGHT_POLL_MS : false;
    },
    refetchIntervalInBackground: true,
  });
};
